import React from "react";
import { Box, Typography, Grid, Divider, Container } from "@mui/material";
import DotPattern from "../assets/Images/Dot2.png"; // optional background
import Logo from "../assets/Images/Dajoo.png"; // replace with actual path
import Header from "../components/Navbar";
import Footer from "../components/Footer";

const Section = ({ title, children }) => (
  <Box mb={5}>
    <Typography variant="h6" fontWeight="bold" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body1" color="textSecondary">
      {children}
    </Typography>
  </Box>
);

const SubSection = ({ title, children }) => (
  <Box sx={{ ml: 2, mb: 2 }}>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body1" color="textSecondary">
      {children}
    </Typography>
  </Box>
);

const PrivacyPolicyPage = () => {
  return (
    <>
      <Header />
      <Box
        sx={{
          backgroundColor: "#F9FAFB",
          backgroundImage: `url(${DotPattern})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "top right",
          backgroundSize: "200px",
          minHeight: "100vh",
          py: 6,
        }}
      >
        <Container maxWidth="md">
          <Grid container justifyContent="center" mb={4}>
            <Typography
              variant="h6"
              noWrap
              component="a"
              href="/"
              sx={{ mr: 2, mt: 1 }}
            >
              <img
                alt="logo"
                src={Logo}
                style={{ height: "120px", borderRadius: "5px" }}
              />
            </Typography>
          </Grid>

          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Effective Date: May 2, 2026
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* 1. IMPORTANT INFORMATION AND WHO WE ARE */}
          <Section title="1. IMPORTANT INFORMATION AND WHO WE ARE">
            <SubSection title="Purpose of this privacy notice">
              This privacy notice aims to give you information on how Wavepalm
              collects and processes your personal data through your use of our
              Platform, including any data you may provide when you create an
              account, join or participate in savings groups, make or receive
              payment, complete identity verification, contact support, interact
              with communications we send, use linked third-party tools or
              services made available through the Platform, or otherwise use our
              services, as well as personal data we generate or infer from such
              use.
              <br />
              <br />
              It is important that you read this privacy notice together with
              any other privacy notice or fair processing notice we may provide
              on specific occasions when we are collecting or processing
              personal data about you, so that you are fully aware of how and
              why we are using your data. This privacy notice supplements other
              notices and privacy notices and is not intended to override them.
            </SubSection>

            <SubSection title="Third-party links">
              This App may include links to third-party websites, plug-ins, and
              applications, and may also enable you to access third-party tools
              or services from within or alongside our Platform. Clicking on
              those links or enabling those connections, submitting a request to
              a third party, or using a third-party tool made available through
              our services may allow third parties to collect, retrieve or
              process data about you. We do not control these third-party
              websites, tools or services and are not responsible for their
              privacy practices and procedures. When you leave our Platform, use
              a third-party service, or interact directly with a third party, we
              encourage you to read the privacy notice and terms of that third
              party. Please note that this section concerns your voluntary
              navigation to or interaction with third-party sites and tools. It
              is separate from, and should be read alongside, the Third-Party
              Processing section below, which sets out the categories of third
              parties with whom we actively share your personal data as part of
              delivering our services.
            </SubSection>
          </Section>

          {/* 2. THE DATA WE COLLECT ABOUT YOU */}
          <Section title="2. THE DATA WE COLLECT ABOUT YOU">
            <Typography variant="body1" color="textSecondary">
              In the course of providing services provided to you through our
              Platform and related channels, we collect information you provide
              us directly, information generated through your use of the
              services, information obtained from your device and browser, and
              information received from third parties such as identity
              verification providers, payment partners, banking or financial
              institution partners, insurers, analytics, hosting and
              communication providers, banking institution or financial
              partners, insurers, analytics, hosting and communication
              providers, and publicly available or regulatory sources, where
              permitted by law. The information we collect may include, but is
              not limited to:
              <br />
              <br />
              <strong>• Identity Data</strong>, such as Name, photograph/photo
              ID, selfie or facial image used for liveness or facial
              verification, National Identification Number (NIN), Bank
              Verification Number (BVN), and details contained in
              government-issued identification documents.
              <br />
              <br />
              <strong>• Contact Data</strong>, such as residential/contact
              address, email address and telephone numbers
              <br />
              <br />
              <strong>• Financial Data</strong>, such as bank account details
              and BVN, payment instrument details, transaction settlement
              details and other financial information reasonably required to
              process payments, payouts, refunds or account verification.
              <br />
              <br />
              <strong>• Transaction Data</strong>, such as details about
              payments to and from you, your contribution history, payout
              information, saving group participation, transaction status,
              funding source, and related account activity.
              <br />
              <br />
              <strong>• Technical Data</strong>, such as internet protocol (IP)
              address, device identifier, your login data, browser type and
              version, time zone setting and location, browser plug-in types and
              versions, operating system and Platform, and other technology on
              the devices you use to access this App.
              <br />
              <br />
              <strong>• Profile Data</strong>, such as your username and
              password, preferences, feedback, survey responses and records
              relevant to risk, fraud, or compliance review.
              <br />
              <br />
              <strong>• Usage Data</strong>, such as information about how you
              use our App, products, and services, including page views, feature
              interactions, session activity, navigation paths, and engagement
              with emails, SMS, push notifications, or in-app messages.
              <br />
              <br />
              <strong>• Marketing and Communication Data</strong>, such as your
              preferences in receiving marketing from us and our third parties,
              and your communication preferences, your consent choices, opt-out
              records, and your preferences for operational, transactional, and
              compliance-related communications.
              <br />
              <br />
              <strong>• Verification and Compliance Data</strong>, such as
              sanctions or politically exposed person screening results, fraud
              checks, repeated failed verification attempts, account or device
              anomaly indicators, and records relating to complaints, disputes,
              investigations, and legal or regulatory requests.
              <br />
              <br />
              We collect Sensitive Personal Data about you, such as biometric
              data (through our facial verification system) and health data.
              However, we do not collect other sensitive information such as
              race, religious or philosophical beliefs, sex life, sexual
              orientation, political opinions, trade union membership, criminal
              convictions and offences.
              <br />
              <br />
              <strong>If you fail to provide personal data</strong>
              <br />
              Where we are required to collect personal data, or need it to
              provide our services to you, and you fail to provide that data
              when requested, we may be unable to provide the service to you.
            </Typography>
          </Section>

          {/* 3. HOW IS YOUR PERSONAL DATA COLLECTED? */}
          <Section title="3. HOW IS YOUR PERSONAL DATA COLLECTED?">
            <Typography variant="body1" color="textSecondary">
              We use different methods to collect data from and about you,
              including through:
              <br />
              <br />
              <strong>• Direct interactions.</strong> You may give us your data
              by filling out forms, creating or updating your account,
              completing verifications steps, making payments, joining or
              managing saving groups, contacting support, responding to surveys,
              submitting complaint or by corresponding with us by phone, email,
              chat, social media or other available means. This includes
              personal data you provide when you:
              <br />
              • apply for our products or services;
              <br />
              • create an account on our Platform;
              <br />
              • request marketing materials or communication to be sent to you;
              <br />
              • make payments using, receive payouts, or use payment features
              through our Platform, or through our payment partners; or
              <br />
              • give us feedback or contact us.
              <br />
              <br />
              <strong>• Automated technologies or interactions.</strong> As you
              interact with our Platform, we will automatically collect
              Technical Data and Usage Data about your equipment, browsing
              actions and patterns. We collect this personal data by using
              cookies and other similar technologies.
              <br />
              <br />
              <strong>• Third parties or publicly available sources.</strong> We
              may also receive and use personal data about you from various
              third parties, including identity verification and security
              providers, payment partners, banks and other financial
              institutions, insurers and insurance intermediaries,
              communications service providers, analytics, hosting, cloud
              storage and infrastructure providers, fraud prevention and risk
              management providers, marketing partners where you have consented
              otherwise permitted by law, and publicly available, regulatory, or
              law enforcement sources.
            </Typography>
          </Section>

          {/* 4. HOW WE USE YOUR PERSONAL DATA */}
          <Section title="4. HOW WE USE YOUR PERSONAL DATA">
            <Typography variant="body1" color="textSecondary">
              We will only use your personal data when the law allows us to do
              so. Depending on the context, we will use your personal data in
              the following circumstances:
              <br />
              <br />
              • When you give us consent.
              <br />
              • To provide our product and services to you
              <br />
              • Where we need to comply with a legal obligation.
              <br />
              • To protect your vital interest or that of another individual.
              <br />
              • Where it is necessary for our legitimate interests (or those of
              a third party), and your interests and fundamental rights do not
              override those interests.
              <br />
              • Where applicable law otherwise permits the processing, including
              where processing is necessary for reasons of substantial public
              interest, the prevention or detection of unlawful activity, or the
              establishment, exercise, or defence of legal claims.
              <br />
              <br />
              We will obtain your consent before sending third-party direct
              marketing communications to you where required by law. You have
              the right to withdraw consent to receive marketing materials at
              any time by contacting us or using the unsubscribe or
              preference-management options made available to you. Withdrawal of
              consent will not affect service-related, transactional, security,
              legal, or other non-marketing communications that we need to send
              in connection with your use of the services.
              <br />
              <br />
              <strong>Purposes for which we will use your personal data</strong>
              <br />
              <br />
              We have set out below, in a table format, a description of the
              main ways we plan to use your personal data, including the
              purposes for which we will disclose it to third parties, and of
              the legal bases we rely on to do so. We have also identified what
              our legitimate interests are, where appropriate.
              <br />
              <br />
              Note that we may process your personal data on more than one
              lawful ground, depending on the specific purpose for which we are
              using your data. Please contact us if you need details about the
              specific lawful ground on which we are relying to process your
              personal data, where more than one ground has been set out in the
              table below.
            </Typography>

            <Box sx={{ mt: 2, overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                  color: "#666",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f0f0f0" }}>
                    <th
                      style={{
                        border: "1px solid #ddd",
                        padding: "10px",
                        textAlign: "left",
                        fontWeight: "bold",
                      }}
                    >
                      Purpose/Activity
                    </th>
                    <th
                      style={{
                        border: "1px solid #ddd",
                        padding: "10px",
                        textAlign: "left",
                        fontWeight: "bold",
                      }}
                    >
                      Lawful basis
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      To provide products and services which you have requested,
                      and to conduct business with you (for example, to register
                      you as a new customer, to process your application, to
                      manage your account, to tell you about important changes
                      to our services, to process and to respond to your
                      enquiries, complaints and issues), to update and improve
                      your records.
                    </td>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      (a) Consent
                      <br />
                      (b) Performance of a contract with you
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      For identity verification, security, fraud prevention,
                      financial information identification and verification
                      purposes.
                    </td>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      (a) Consent
                      <br />
                      (b) Compliance with a legal obligation (e.g., compliance
                      with our Anti-Money Laundering, Know Your Customer, fraud
                      prevention sanction screening and other regulatory
                      obligations).
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      To detect, investigate, prevent and prosecute criminal
                      activity and to meet our regulatory, legal and compliance
                      obligations (including performing regulatory and
                      prudential compliance checks on an ongoing basis, account
                      and transaction monitoring, checks for sanctions and
                      politically exposed persons, and transaction and tax
                      reporting). Also making disclosures to, co-operating with,
                      and complying with requests from public authorities,
                      regulators, courts of law, tax authorities, governmental
                      bodies or law enforcement agencies, and investigating and
                      preventing fraud, terrorism and other crimes.
                    </td>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      (a) Compliance with a legal obligation
                      <br />
                      (b) Legitimate interests (ensuring effective compliance in
                      all relevant jurisdictions, protecting our customers,
                      maintaining a good relationship with law enforcement,
                      regulators and other relevant authorities. Also, we have a
                      legitimate interest in protecting our legal rights).
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      To improve customer service, and to analyse how our
                      Platform is used, troubleshoot issues, monitor
                      performance, develop and improve features and decide if a
                      product or service is suitable for your needs.
                    </td>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      Legitimate interests (we have a legitimate interest in
                      providing quality customer service, as this allows us to
                      maintain good client relationships and to deal with
                      queries and complaints effectively. Also, we have a
                      legitimate interest in ensuring that we provide our
                      customers with appropriate products and services, to
                      ensure both appropriate risk management and good client
                      relationship management).
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      To provide you with the appropriate level of service, to
                      accommodate your specific individual needs and treat you
                      fairly based on any vulnerability you may have, whether
                      you have told us, or we believe it necessary to record, to
                      improve customer service and ensure equal treatment,
                      protect vulnerable customers, or safeguard your economic
                      well-being.
                    </td>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      (a) Consent
                      <br />
                      (b) Vital Interest
                      <br />
                      (c) Legitimate interests (we have a legitimate interest in
                      providing quality customer service, as this allows us to
                      maintain good client relationships and to deal with
                      queries and complaints effectively).
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      To carry out operational and administrative functions
                      (e.g. to maintain our own accounts and records, operate
                      information technology systems, host and secure our
                      infrastructure, to carry out billing-related and payments
                      administration, deliver email, SMS, push notifications and
                      one-time passwords, manage claims or insurance related
                      matters where applicable, and administer our business and
                      internal reporting).
                    </td>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      (a) Compliance with a legal obligation (to maintain
                      certain statutory records)
                      <br />
                      (b) Legitimate interests (we have a legitimate interest in
                      running our business efficiently).
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      To share relevant information in respect of a prospective
                      or actual sale, merger, acquisition, financing,
                      reorganization or transfer involving Wavepalm, or any of
                      its affiliates, or any part of its or their business, in
                      each case subject to appropriate confidentiality measures.
                    </td>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      Legitimate interests (we have a legitimate interest in
                      complying with due diligence requirements and being
                      efficient in how we run the business).
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      Marketing: To tell you about products and services that
                      may interest you (by email, phone SMS, push notification,
                      in app message or other permitted channel in accordance
                      with your preferences and applicable law).
                    </td>
                    <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                      (a) Consent
                      <br />
                      (b) Legitimate interests (we have a legitimate interest in
                      making our customers aware of our other services and
                      offerings).
                    </td>
                  </tr>
                </tbody>
              </table>
            </Box>

            <Typography variant="body1" color="textSecondary" sx={{ mt: 3 }}>
              <strong>Marketing</strong>
              <br />
              We strive to provide you with choices regarding certain personal
              data uses, particularly around marketing and advertising. You have
              the right to revoke your consent to such uses at any time by
              contacting us or adjusting your preferences through our
              communication channels. We are committed to respecting your
              choices and ensuring that your data is handled in accordance with
              your preferences.
              <br />
              <br />
              <strong>Promotional offers from us</strong>
              <br />
              We may use your Identity, Contact, Technical, Usage and Profile
              Data to form an opinion on what we think you may want or need, or
              what may be of interest to you. This is how we decide which
              products, services and offers may be relevant for you (we call
              this marketing).
              <br />
              <br />
              We will send you marketing communications where you have given
              your consent to receive them. You may withdraw your consent or opt
              out of receiving such communications at any time by following the
              unsubscribe instructions provided in each message or by contacting
              us directly.
            </Typography>
          </Section>

          {/* 5. THIRD PARTY PROCESSING */}
          <Section title="5. THIRD PARTY PROCESSING">
            <Typography variant="body1" color="textSecondary">
              We may share your personal data, where necessary and in accordance
              with applicable law, with the categories of recipients listed
              below. Please note that data sharing with certain recipients -
              such as identity verification providers, payment partners,
              financial institutions, insurers, and regulatory or law
              enforcement authorities - occurs as part of the ordinary operation
              of our services or as required by law, and is not subject to a
              marketing opt-out. Data sharing for marketing purposes is subject
              to the separate consent and opt-out provisions described in the
              Marketing and the Opting Out sections of this Notice.
              <br />
              <br />
              The categories of recipients with whom we may share your personal
              data are: identity verification and security providers; payment
              partners, banks and other financial institutions; insurers,
              insurance intermediaries, claims handlers and professional
              advisers; communications providers that deliver email, SMS, voice
              calls, push notifications and one-time passwords; analytics,
              hosting, cloud, infrastructure, monitoring and providers; fraud
              prevention, risk management and sanctions-screening providers;
              regulators, governmental authorities, law enforcement agencies,
              courts and dispute-resolution bodies; prospective or actual
              investors, purchasers and transaction counterparties and their
              advisers in connection with a corporate transaction; and other
              third parties where you ask us to share information or where
              sharing is necessary to provide the services you request.
              <br />
              <br />
              Some third parties process personal data on our behalf and only in
              accordance with our instructions, while others may process
              personal data as independent controllers where you interact with
              them directly, use their services, or are redirected to their
              websites, applications or tools. In those cases, their own terms
              and privacy notices will also apply to their handling of your
              personal data.
              <br />
              <br />
              Examples of how we may share your personal data include sharing
              identity and verification information with identity verification
              providers to verify your NIN, BVN, facial image or
              government-issued identification; sharing transaction and account
              information with payment partners and financial institutions to
              process contributions, payouts, reversals, refunds and settlement;
              sharing relevant information with insurers or claims-related
              parties where an incident, claim, complaint, dispute or recovery
              matter arises; sharing technical and usage information with
              analytics, hosting, communications and security providers to
              operate, secure, support and improve the App; and sharing
              information with competent authorities or other relevant parties
              where required for legal compliance, fraud prevention,
              investigation, or the protection of our users, services or rights.
              <br />
              <br />
              <strong>Opting out</strong>
              <br />
              You can ask us or third parties sending marketing on our behalf to
              stop sending you marketing messages at any time by following the
              unsubscribe instructions provided in each message, adjusting your
              preferences where available, or contacting us.
              <br />
              <br />
              This opt-out right applies to marketing communications only. It
              does not affect the sharing of your personal data with third
              parties where such sharing is necessary for the delivery of our
              services, the processing of transactions, identity verification,
              fraud prevention, regulatory compliance, or any other operational
              or legally required purpose as described in the Third-Party
              Processing section of this Notice.
              <br />
              <br />
              <strong>Cookies</strong>
              <br />
              We may use cookies to enhance user experience, analyze Platform
              performance and provide tailored features. This enables the
              application to store information about your preferences or device
              usage and ensure seamless Platform functionality.
              <br />
              <br />
              There are necessary cookies that may be used in limited
              circumstances (such as accessing our website). You can manage the
              optional cookies when using the tracking technology through your
              device settings or within the Platform settings.
              <br />
              <br />
              Note that disabling these features may impact your ability to
              access certain functionalities of the Platform.
              <br />
              <br />
              <strong>Change of purpose</strong>
              <br />
              We will only use your personal data for the purposes for which we
              collected it, unless we reasonably consider that we need to use it
              for another reason and that reason is compatible with the original
              purpose. If you wish to get an explanation as to how the
              processing for the new purpose is compatible with the original
              purpose, please contact us.
              <br />
              <br />
              If we need to use your personal data for an unrelated purpose, we
              will notify you and we will explain the legal basis that allows us
              to do so.
              <br />
              <br />
              Please note that we may process your personal data without your
              knowledge or consent, where it is required or permitted by law.
            </Typography>
          </Section>

          {/* 6. INTERNATIONAL TRANSFERS */}
          <Section title="6. INTERNATIONAL TRANSFERS">
            <Typography variant="body1" color="textSecondary">
              Your personal data is hosted and processed in data centres
              operated by our cloud service providers located in [the United
              Kingdom]. As our servers, cloud infrastructure, and data storage
              facilities are located outside Nigeria, any personal data you
              provide to us will be transferred to, stored in, and processed in
              countries other than Nigeria. By registering for and using our
              Platform, you acknowledge that your personal data will be
              transferred across international borders as described in this
              section.
              <br />
              <br />
              We transfer your personal data outside Nigeria in compliance with
              the Nigeria Data Protection Act, 2023 (NDPA) and the Nigeria Data
              Protection Act, 2023: General Application and Implementation
              Directive (GAID) 2025, as supervised by the Nigeria Data
              Protection Commission (NDPC). We rely on one or more of the
              following legal bases for such transfers:
              <br />
              • The recipient country, territory, or international organisation
              has been determined by the NDPC to provide an adequate level of
              protection for personal data;
              <br />
              • You have given your explicit consent to the transfer after being
              informed of the possible risks;
              <br />
              • The transfer is necessary for the performance of the contract
              between you and us, or for the implementation of pre-contractual
              measures taken at your request;
              <br />
              • The transfer is necessary for the conclusion or performance of a
              contract concluded in your interest between us and a third party;
              or
              <br />
              • The transfer is necessary for reasons of public interest, the
              establishment, exercise, or defence of legal claims, or to protect
              your vital interests where you are physically or legally incapable
              of giving consent.
              <br />
              <br />
              To protect your personal data when it is transferred
              internationally, we implement appropriate technical,
              organisational, and contractual safeguards, including:
              <br />
              • Entering into data processing agreements with all third-party
              service providers that contain enforceable data protection
              obligations consistent with the NDPA and, where applicable,
              international frameworks such as the EU General Data Protection
              Regulation (GDPR);
              <br />
              • Encrypting personal data in transit (using TLS or equivalent
              protocols) and at rest;
              <br />
              • Applying strict access controls, multi-factor authentication,
              and the principle of least privilege to limit who can access your
              data;
              <br />
              • Conducting due diligence and periodic audits of our cloud
              providers and sub-processors to ensure ongoing compliance with
              applicable data protection standards;
              <br />
              • Pseudonymising or anonymising personal data where feasible; and
              <br />
              • Maintaining incident response and breach notification procedures
              in line with the NDPA.
              <br />
              <br />
              You should be aware that data protection laws in countries outside
              Nigeria may differ from, and in some cases may offer less
              protection than, Nigerian law. Foreign authorities, courts, law
              enforcement, or regulators in the destination jurisdictions may,
              in certain circumstances, be entitled to access personal data
              transferred to those jurisdictions. We take reasonable steps to
              challenge disproportionate or unlawful requests for access to your
              data.
              <br />
              <br />
              You retain all rights granted to you under the NDPA regardless of
              where your personal data is processed, including the rights to
              access, rectify, erase, restrict, or object to the processing of
              your data, and the right to data portability. You also have the
              right to withdraw any consent you have given to the international
              transfer of your data at any time, although such withdrawal will
              not affect the lawfulness of processing carried out before the
              withdrawal. Where withdrawal of consent makes it impossible for us
              to continue providing the Platform's services to you, we will
              inform you of the consequences.
            </Typography>
          </Section>

          {/* 7. DATA SECURITY */}
          <Section title="7. DATA SECURITY">
            <Typography variant="body1" color="textSecondary">
              We have put in place appropriate security measures to prevent your
              personal data from being accidentally lost, used or accessed in an
              unauthorised way, altered or disclosed. In addition, we limit
              access to your personal data to those employees, agents,
              contractors and other third parties who have a business need to
              know. They will only process your personal data on our
              instructions, and they are subject to a duty of confidentiality.
              <br />
              <br />
              We have put in place procedures to deal with any suspected
              personal data breach and will notify you and any applicable
              regulator of a breach where we are legally required to do so.
            </Typography>
          </Section>

          {/* 8. DATA RETENTION */}
          <Section title="8. DATA RETENTION">
            <Typography variant="body1" color="textSecondary">
              We will only retain your personal data for as long as reasonably
              necessary to fulfil the purposes for which we collected it,
              including for the purpose of satisfying any legal, regulatory,
              tax, accounting or reporting requirements. We may retain your
              personal data for a longer period in the event of a complaint or
              if we reasonably believe there is a prospect of litigation in
              respect of our relationship with you.
              <br />
              <br />
              To determine the appropriate retention period for personal data,
              we consider the amount, nature and sensitivity of the personal
              data, the potential risk of harm from unauthorised use or
              disclosure of your personal data, the purposes for which we
              process your personal data and whether we can achieve those
              purposes through other means, and the applicable legal and
              regulatory requirements.
              <br />
              <br />
              In some circumstances we will anonymise your personal data (so
              that it can no longer be associated with you) for research or
              statistical purposes, in which case we may use this information
              indefinitely without further notice to you.
            </Typography>
          </Section>

          {/* 9. YOUR LEGAL RIGHTS */}
          <Section title="9. YOUR LEGAL RIGHTS">
            <Typography variant="body1" color="textSecondary">
              You have rights under data protection laws in relation to your
              personal data. You may:
              <br />
              <br />
              • request access to your personal data
              <br />
              • request correction of your personal data
              <br />
              • request erasure of your personal data
              <br />
              • object to processing of your personal data
              <br />
              • request restriction of processing your personal data
              <br />
              • request transfer of your personal data
              <br />
              • withdraw consent
              <br />
              • lodge a complaint with the relevant authority
              <br />
              <br />
              If you wish to exercise any of the rights set out above, except
              the right to lodge a complaint with the relevant authority, please
              contact our Data Protection Officer:
              <br />
              <br />
              [provide data protection officer's contact details]
            </Typography>
          </Section>

          {/* 10. CHANGES TO THE PRIVACY NOTICE */}
          <Section title="10. CHANGES TO THE PRIVACY NOTICE">
            <Typography variant="body1" color="textSecondary">
              Our Privacy Notice may be updated from time to time. We will post
              notice of such changes on this page and, if the changes are
              significant, we will provide a more prominent notice. We will also
              keep prior versions of this Privacy Notice in archive for your
              review.
            </Typography>
          </Section>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default PrivacyPolicyPage;
